import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";

import { SOUND_NAME } from "../../../sounds";
import { PixiSettings } from "../../../pixiSettings";
import anime from "animejs/lib/anime.es.js";
import WinAnimationController from "./WinAnimationController";
import TextButton from "../../UserInterface/primary/Buttons/TextButton";
import {
  selectWinAnimationsPlaying,
  selectBet,
  selectCurrentRound,
  selectFreeSpinActive,
} from "../../../State/selectors";
import { soundManager } from "../../../SoundManager";

// Overlay settings (matching WarningPopup style)
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_FADE_DURATION = 350;

const WinAnimation = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const winControllerRef = useRef(null);
  const winContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const clickIndicatorRef = useRef(null);
  const buttonRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;


  // Overlay state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef (same API as Overlay component)
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Determine button text based on device type
  const continueButtonKey = useMemo(() => {
    return device === PixiSettings.DEVICE.DESKTOP
      ? "click_to_continue"
      : "tap_to_continue";
  }, [device]);

  // Get win animations playing state and actual game data
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const bet = useSelector(selectBet);
  const currentRound = useSelector(selectCurrentRound);
  const freeSpinActive = useSelector(selectFreeSpinActive);

  // Use win amount from current round (same format for base game and free spins)
  const winAmount = currentRound?.win || 0;

  // Initialize WinAnimationController when assets are ready
  useEffect(() => {
    if (winContainerRef.current && !winControllerRef.current) {
      // Set button alpha to 0 for this instance
      if (buttonRef.current) {
        buttonRef.current.alpha = 0;
      }

      winControllerRef.current = new WinAnimationController(
        winContainerRef.current,
        dimensionsContext,
        overlayRef,
        dispatch,
        uiSymbolSize,
        uiSymbolSize,
        clickIndicatorRef,
        buttonRef,
        t,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (winControllerRef.current && uiSymbolSize && dimensionsContext) {
      winControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Handle user click to continue animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (winControllerRef.current && winAnimationsPlaying) {
        const accepted = winControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [winAnimationsPlaying],
  );

  // Add spacebar handling - ensure this takes priority over other handlers
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when animations are playing AND controller exists
      if (!winAnimationsPlaying || !winControllerRef.current) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Emergency recovery check
      try {
        const accepted = winControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch (error) {
        // Emergency recovery - try to finish animation
        if (
          winControllerRef.current &&
          winControllerRef.current.finishAnimation
        ) {
          winControllerRef.current.finishAnimation();
        }
      }
    };

    // Use capture phase to ensure we get the event first
    if (winAnimationsPlaying) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [winAnimationsPlaying, dispatch]);



  // Start animation when winAnimationsPlaying becomes true and we have a valid win
  useEffect(() => {
    if (winAnimationsPlaying && winControllerRef.current && winAmount > 0) {
      // Use the bet stored inside the round (the bet that actually produced this win).
      // Falls back to current Redux bet for live spins where round.bet may not be set yet.
      const roundBet = currentRound?.bet || bet;
      winControllerRef.current.startSequence(winAmount, roundBet);
    } else if (!winAnimationsPlaying && winControllerRef.current) {
      // Stop animation when state becomes false
      winControllerRef.current.stopWinAnimation();
    }
  }, [
    winAnimationsPlaying,
    winAmount,
    bet,
    freeSpinActive,
    currentRound,
  ]);

  // Cleanup overlay animation on unmount
  useEffect(() => {
    return () => {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position to prevent recalculation on every render
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function (WarningPopup style)
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="win_animation"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={10}
      eventMode={winAnimationsPlaying ? "auto" : "none"}
      ref={winContainerRef}
    >
      {/* Background overlay - inline Graphics (WarningPopup pattern) */}
      <Graphics
        name="win_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={winAnimationsPlaying ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        cursor={winAnimationsPlaying ? "pointer" : "default"}
      />

      {/* Win spine and counter are managed internally by WinAnimationController */}

      <TextButton
        ref={buttonRef}
        y={containerPosition.y}
        translationKey={continueButtonKey}
      />
    </Container>
  );
};

export default WinAnimation;
